// 2026-05-26 - FormuLearning - Step 5 §5.3 DoE 케이스 테이블
// 파일 위치: frontend/src/features/step5/CaseTableSection.tsx
// 2026-11-03 수정 - 이슈 #114: viewer 는 Generate Cases·Upload Y·Y 값 입력 비활성(Template 내려받기는 열어 둔다).
// 2026-09-23 수정 - 이슈 #85: 생성 불가 사유가 있으면 Generate 버튼을 막고 사유를 보인다.

import { useRef } from 'react'
import type { DoECase } from '../../api/step5'
import { useCanWrite, READ_ONLY_TITLE } from '../../hooks/useCanWrite'

interface Props {
  cases: DoECase[]
  onCasesChange: (c: DoECase[]) => void
  onGenerate: () => Promise<void>
  generating: boolean
  blockReason: string | null
  templateUrl: string | null
  onUploadY: (file: File) => Promise<void>
  uploading: boolean
}

export default function CaseTableSection({ cases, onCasesChange, onGenerate, generating, blockReason, templateUrl, onUploadY, uploading }: Props) {
  const factorKeys = cases[0] ? Object.keys(cases[0].factors) : []
  const responseKeys = cases[0] ? Object.keys(cases[0].responses) : []
  const fileRef = useRef<HTMLInputElement>(null)
  const canWrite = useCanWrite()

  const updateResponse = (caseIdx: number, key: string, val: number | null) => {
    const next = [...cases]
    next[caseIdx] = { ...next[caseIdx], responses: { ...next[caseIdx].responses, [key]: val } }
    onCasesChange(next)
  }

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) onUploadY(file)
  }

  return (
    <div className="mb-4">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
          DoE Cases
        </h4>
        <button
          onClick={onGenerate}
          disabled={generating || !!blockReason || !canWrite}
          title={canWrite ? blockReason ?? undefined : READ_ONLY_TITLE}
          className="rounded bg-accent-teal px-3 py-1.5 text-[11px] font-semibold text-[#1a1b1e] transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {generating ? 'Generating...' : 'Generate Cases'}
        </button>
      </div>
      {blockReason && <p className="mb-2 text-[10px] text-text-secondary">{blockReason}</p>}

      {cases.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3">
          {templateUrl && (
            <a
              href={templateUrl}
              className="rounded px-3 py-1.5 text-[11px] font-medium text-accent-teal transition-colors hover:bg-bg-input"
              style={{ border: '1px solid var(--border-teal)' }}
              download
            >
              Template ↓
            </a>
          )}
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading || !canWrite}
            title={canWrite ? undefined : READ_ONLY_TITLE}
            className="rounded bg-accent-purple px-3 py-1.5 text-[11px] font-semibold text-[#1a1b1e] transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {uploading ? 'Uploading...' : 'Upload Y values'}
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} className="hidden" />
        </div>
      )}

      {cases.length > 0 ? (
        <div className="overflow-x-auto rounded border" style={{ borderColor: 'var(--border-subtle)' }}>
          <table className="w-full text-[11px]">
            <thead>
              <tr style={{ backgroundColor: 'var(--color-bg-panel)' }}>
                <th className="px-2 py-1.5 text-center font-medium text-text-secondary">Run</th>
                {factorKeys.map((k) => (
                  <th key={k} className="px-2 py-1.5 text-center font-medium text-text-secondary">{k}</th>
                ))}
                {responseKeys.map((k) => (
                  <th key={k} className="px-2 py-1.5 text-center font-medium text-text-secondary" style={{ color: 'var(--color-accent-teal)' }}>{k}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cases.map((c, ci) => (
                <tr key={ci} className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                  <td className="px-2 py-1.5 text-center font-medium text-text-body">R{String(c.run).padStart(2, '0')}</td>
                  {factorKeys.map((k) => (
                    <td key={k} className="px-2 py-1.5 text-center text-text-body">{typeof c.factors[k] === 'number' ? c.factors[k].toFixed(2) : '—'}</td>
                  ))}
                  {responseKeys.map((k) => (
                    <td key={k} className="px-1 py-1">
                      <input
                        type="number"
                        value={c.responses[k] ?? ''}
                        disabled={!canWrite}
                        title={canWrite ? undefined : READ_ONLY_TITLE}
                        onChange={(e) => updateResponse(ci, k, e.target.value ? Number(e.target.value) : null)}
                        className="w-full rounded bg-bg-input px-2 py-0.5 text-center text-[11px] text-text-body outline-none"
                        placeholder="—"
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-[11px] text-text-secondary">Select a DoE method and define variables, then generate cases.</p>
      )}
    </div>
  )
}
